/**
 * SIMS-9202 / SIMS-9203 — what "Enrollments per person" seeds when an administrator
 * turns it on.
 *
 * Extracted from the page so the rule is unit-testable: the two fields seed by
 * DIFFERENT rules, and the difference is easy to flatten back into one by accident.
 */

export interface EnrollmentsPerPersonSeed {
	enrollmentsPerPerson?: number;
	enrollmentsPerPersonAcrossOrgUnits?: boolean;
}

/**
 * Applies the on-enable seed, mutating and returning the given parameters object.
 * Called only on the OFF -> ON transition; nothing is written onto programs that
 * leave the feature off, which is what keeps FR-13 true (no backfill, no migration).
 *
 * The maximum is seeded only when ABSENT (SIMS-9202): a program that already carries
 * one keeps it, and no acceptance criterion asks for a particular starting number.
 *
 * The org-unit scope is seeded UNCONDITIONALLY (SIMS-9203 AC2). AC2 states it with no
 * precondition — "when the administrator turns it on ... it starts set to count across
 * every organization unit" — and its Given is simply "a program with Enrollments per
 * person turned off", which includes a program that was scoped to a single unit before
 * being turned off: exactly the state AC4 leaves behind. Preserving a stored `false`
 * would make re-enabling such a program come back scoped, contradicting AC2.
 *
 * The stored value still survives being turned off — AC4 asks only that the control
 * stop being available, and nothing deletes the key. The next enable resets it.
 */
export function seedEnrollmentsPerPersonOnEnable<
	T extends EnrollmentsPerPersonSeed,
>(parameters: T): T {
	if (parameters.enrollmentsPerPerson === undefined) {
		parameters.enrollmentsPerPerson = 1;
	}
	parameters.enrollmentsPerPersonAcrossOrgUnits = true;
	return parameters;
}
